<template>
  <div ref="detail" class="card-detail-wrapper card-full-width-mobile mx-auto mt-7 mt-lg-8">
    <div class="py-10 align-items-center" v-if="!quote">
      <loader></loader>
    </div>
    <article
      v-else
      ref="detailCard"
      class="card card-quote-details mb-2"
      id="quote-proposal-detail"
      sticky-container
    >
      <ribbon v-if="trsMode && !orderPage" :text="content.trs_pv_package_ribbon"></ribbon>
      <ribbon v-if="isOpenRedesign && hasActiveStage" :text="content.redesign_card_ribbon"></ribbon>

      <div class="card-body">
        <h2
          v-if="multipleQuotes"
          class="desktop_show mx-md-5 mb-4 card-title text-primary font-weight-bold"
          v-dynamic-content:pv_package_information_title_selectedoffer="[quotePosition(quote)]"
        ></h2>
        <div v-if="!isGeneric" class="card-detail-header mx-md-5 mx-0 desktop_show">
          <div
            v-for="(tab, idx) in tabs"
            :key="tab.key"
            @click="selectTab(idx)"
            class="card-tab text-center"
            :class="{ selected: idx == selectedTab }"
          >
            <span v-dynamic-content="{ rawText: content[tab.key] }"></span>
          </div>
        </div>
        <div class="mx-0 mx-md-5">
          <h2
            v-if="!multipleQuotes"
            class="card-title text-primary font-weight-bold pt-7 desktop_hide"
            v-dynamic-content:pv_package_information_title_singleoffer
          ></h2>
          <h2
            v-else
            class="card-title text-primary font-weight-boldb pt-7 desktop_hide"
            v-dynamic-content:pv_package_information_title_selectedoffer="[quotePosition(quote)]"
          ></h2>
          <div class="detail-item">
            <component :is="currentTabComponent" :id="id" :quote="quote" class="desktop_show" />
            <component
              :is="tabs[0].component"
              id="id"
              :quote="quote"
              :desktop="false"
              class="desktop_hide"
            />
          </div>
        </div>
        <Collapse v-if="!isGeneric" :quote="quote" class="desktop_hide" />
        <div
          class="mx-2 mx-md-1 font-size-sm mt-7 text-muted"
          v-if="showDetailedCalculationDisclaimer"
          v-dynamic-content:detail_calculation_disclaimer
        ></div>
      </div>

      <sticky-cta :withSteps="$mq === 'xl' && !isGeneric" v-if="!this.orderPage">
        <b-button class="btn-block mb-1" v-if="!trsMode" @click="selectOrder" variant="primary">
          {{ ctaButtonText }}
        </b-button>
      </sticky-cta>

      <div class="d-flex justify-content-between flex-wrap align-items-end desktop_hide">
        <backto-top-button class="desktop_hide"></backto-top-button>
      </div>
    </article>
    <div v-if="orderPage" class="d-flex justify-content-start">
      <router-link
        :to="{ name: 'Order' }"
        class="back-link d-flex justify-content-center align-items-center font-weight-bold"
        v-dynamic-content:payment_detail_back_link
      ></router-link>
    </div>
  </div>
</template>

<script>
import BacktoTopButton from '@/components/main/scroll/BacktoTopButton'
import Collapse from '@/components/parts/Collapse'
import Ribbon from '@/components/parts/Ribbon'
import DetailQuote from '@/components/quotes/DetailQuote'
import DetailCalculation from '@/components/quotes/DetailCalculation'
import DetailProductsWarranties from '@/components/quotes/DetailProductsWarranties'
import DetailEnvironment from '@/components/quotes/DetailEnvironment'
import DetailInstallationMonitoring from '@/components/quotes/DetailInstallationMonitoring'
import DetailAdditionalLabor from '@/components/quotes/DetailAdditionalLabor'
import DetailFinancing from '@/components/quotes/DetailFinancing'
import StickyCta from '@/components/parts/StickyCta'
import { mapGetters } from 'vuex'

export default {
  props: ['id'],
  name: 'CardDetail',
  components: {
    Collapse,
    DetailCalculation,
    DetailQuote,
    DetailProductsWarranties,
    DetailEnvironment,
    DetailInstallationMonitoring,
    DetailAdditionalLabor,
    Ribbon,
    BacktoTopButton,
    StickyCta,
  },
  data() {
    return {
      selectedTab: 0,
      lastId: null,
    }
  },
  computed: {
    ...mapGetters(['trsFormSentMode', 'isGeneric', 'isOpenRedesign']),
    orderPage() {
      return this.$route.name === 'OrderDetail'
    },
    isPreviouslyChosenQuoteInRedesignQuote() {
      return this.id === this.$store.getters.iquote.chosenQuoteId
    },
    ctaButtonText() {
      if (this.isOpenRedesign)
        return this.isPreviouslyChosenQuoteInRedesignQuote
          ? this.content.redesign_card_current_button
          : this.content.redesign_card_new_button
      else return this.content.pv_package_cta_choose
    },
    tabs() {
      const tabs = {
        pakket: { key: 'pv_tab_header_view_system', component: DetailQuote },
        // 2023-05-20: Temp removed detailed calculation tab because backend is not ready
        // calculation: { key: 'pv_tab_header_detailed_calculation', component: DetailCalculation },
        financing: { key: 'pv_tab_header_financing', component: DetailFinancing },
        warranties: {
          key: 'pv_infoblock_productsandwarranties',
          component: DetailProductsWarranties,
        },
        monitoring: {
          key: 'pv_infoblock_installationandmonitoring',
          component: DetailInstallationMonitoring,
        },
        environment: { key: 'pv_infoblock_environmental', component: DetailEnvironment },
        addedLabor: {
          key: 'pv_infoblock_additional_labor_costs',
          component: DetailAdditionalLabor,
        },
      }

      const show = {
        pakket: true,
        // calculation: !this.trsMode && !this.showTabForFlanders,
        financing: this.$store.getters.showFinancingSection,
        warranties: true,
        monitoring: true,
        environment: true,
        addedLabor: this.$store.getters.showAddedLaborSection,
      }

      return Object.keys(show)
        .filter((k) => show[k])
        .map((tab) => tabs[tab])
    },
    currentTabComponent() {
      return this.tabs[this.selectedTab].component
    },
    showDetailedCalculationDisclaimer() {
      return this.tabs[this.selectedTab].key === 'pv_tab_header_detailed_calculation'
    },
    trsFormSentMode() {
      return this.trsFormSentMode
    },
    quote() {
      this.$store.dispatch('putActivity', {
        activity: 'VIEW_PV',
        quoteId: this.id,
      })
      return this.orderPage ? this.chosenQuote : this.quotes.find((x) => x.id === this.id)
    },
    hasActiveStage() {
      return ['sent'].includes(this.quote.stage)
    },
    trsCollapseOfferText() {
      return {
        collapseBtnText: this.content.trs_collapse_offer_btn_text,
        paragraphs: [this.content.trs_collapse_offer_paragraph1],
        smallText: this.content.trs_collapse_offer_smalltext,
        placeholder: this.content.trs_collapse_offer_placeholder_input,
        ctaBtnText: this.content.trs_collapse_offer_cta_btn,
      }
    },
    trsCollapseEditText() {
      return {
        collapseBtnText: this.content.trs_collapse_edit_btn_text,
        paragraphs: [
          this.content.trs_collapse_edit_paragraph1,
          this.content.trs_collapse_edit_paragraph2,
        ],
        smallText: this.content.trs_collapse_edit_smalltext,
        placeholder: this.content.trs_collapse_edit_placeholder_input,
        ctaBtnText: this.content.trs_collapse_edit_cta_btn,
      }
    },
  },
  watch: {
    quote() {
      this.scrollIntoView()
    },
  },
  mounted() {
    this.scrollIntoView()
    if (this.orderPage) return
    this.$store.commit('RESET_SERVICE_PRODUCTS_SERVICES')
    // when there are quotes but the chosen quote is not present navigate to step1,
    // since the quote is not available anymore.
    if (this.quotes && !this.quote) {
      this.$router.push({ name: 'ContentProposal' })
    }
   
    window.dataLayer.push({
      event: 'page_load',
      element: 'system detail',
      quote_type: this.$store.getters.quoteType,
    })
  },
  updated() {
    if (this.orderPage) return
    if (this.lastId !== this.id) {
      this.lastId = this.id
    }
  },
  methods: {
    scrollIntoView() {
      this.$refs.detail.scrollIntoView({ behavior: 'smooth' })
    },
    selectTab(idx) {
      this.selectedTab = idx
    },
    contentFilters(contentKey, filterOptions) {
      return this.$options.filters.replaceDynamicContent(contentKey, filterOptions)
    },
    selectOrder() {
      this.$router.push({
        name: 'OrderSummary',
        params: { id: this.quote.id },
      })
    },
  },
}
</script>
